import React, { useState, useEffect } from 'react';
import { Sparkles, Plus, AlertTriangle, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

const SECTOR_BENCHMARKS = {
  Software:    { dso: 45, dpo: 30, dio: null },
  Healthcare:  { dso: 60, dpo: 40, dio: 20  },
  Manufacturing:{ dso: 50, dpo: 45, dio: 60  },
  Retail:      { dso: 15, dpo: 35, dio: 45  },
  default:     { dso: 45, dpo: 35, dio: 30  },
};

const DEFAULT_TAX_FLAGS = [
  { id: 'bonus_depreciation',   label: 'Bonus depreciation window',               status: 'monitor' },
  { id: 'rd_deduction',         label: 'R&D expense deduction eligibility',        status: 'monitor' },
  { id: 'interest_limit',       label: 'Interest deductibility limitation (163j)', status: 'monitor' },
  { id: 'pte_election',         label: 'Pass-through entity tax election',         status: 'monitor' },
  { id: 'working_capital',      label: 'Working capital release opportunity',      status: 'monitor' },
];

const TAX_STATUS_CONFIG = {
  monitor:        { label: 'Monitor',       cls: 'pi-badge-blue'  },
  action_needed:  { label: 'Action Needed', cls: 'pi-badge-red'   },
  resolved:       { label: 'Resolved',      cls: 'pi-badge-green' },
};

function freshnessInfo(updatedAt) {
  if (!updatedAt) return { text: 'No data entered yet', color: '#94a3b8' };
  const days = Math.floor((Date.now() - new Date(updatedAt).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return { text: 'Updated today', color: '#16a34a' };
  if (days <= 30) return { text: `Updated ${days} day${days === 1 ? '' : 's'} ago`, color: '#16a34a' };
  if (days <= 90) return { text: `Updated ${days} days ago — consider refreshing`, color: '#d97706' };
  const dateStr = new Date(updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return { text: `Last updated ${dateStr} — data may be stale`, color: '#dc2626' };
}

function WCCard({ label, value, benchmark, unit = 'days' }) {
  const diff = value != null && benchmark != null ? value - benchmark : null;
  const isGood = diff != null ? diff <= 0 : null;
  return (
    <div className="pi-stat-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="pi-stat-value" style={{ fontSize: 22 }}>{value != null ? value : '—'}</div>
        {diff != null && (
          <span className={`pi-badge ${isGood ? 'pi-badge-green' : 'pi-badge-amber'}`} style={{ fontSize: 10 }}>
            {diff > 0 ? '+' : ''}{diff} vs benchmark
          </span>
        )}
      </div>
      <div className="pi-stat-label">{label}</div>
      {benchmark != null && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Benchmark: {benchmark} {unit}</div>}
    </div>
  );
}

export default function PICashTaxTab({ portco, currentUser }) {
  const navigate = useNavigate();
  const [wc, setWC] = useState({ dso: null, dpo: null, dio: null, dso_updated_at: null, dpo_updated_at: null, dio_updated_at: null });
  const [wcDraft, setWCDraft] = useState({ dso: '', dpo: '', dio: '' });
  const [wcDirty, setWCDirty] = useState({ dso: false, dpo: false, dio: false });
  const [wcSaved, setWCSaved] = useState({ dso: false, dpo: false, dio: false });
  const [debtSchedule, setDebtSchedule] = useState([]);
  const [taxFlags, setTaxFlags] = useState(DEFAULT_TAX_FLAGS);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [showAddDebt, setShowAddDebt] = useState(false);
  const [newDebt, setNewDebt] = useState({ period: '', beginning_balance: '', scheduled_payment: '', actual_payment: '', ending_balance: '', covenant_headroom: '' });

  const canEdit = hasPI(currentUser, 'pi.cash.edit');
  const sector = portco.sector || 'default';
  const benchmarks = SECTOR_BENCHMARKS[sector] || SECTOR_BENCHMARKS.default;

  const allHaveValues = wc.dso != null && wc.dpo != null && wc.dio != null;
  const ccc = allHaveValues ? wc.dso + wc.dio - wc.dpo : null;

  const allNull = wc.dso == null && wc.dpo == null && wc.dio == null;
  const anyStale = ['dso', 'dpo', 'dio'].some(k => {
    if (wc[k] == null) return false;
    const ts = wc[`${k}_updated_at`];
    if (!ts) return true;
    return Math.floor((Date.now() - new Date(ts).getTime()) / (1000 * 60 * 60 * 24)) > 90;
  });

  useEffect(() => {
    Promise.all([
      piApi.getCashData(portco.id),
      piApi.getDebtSchedule(portco.id),
    ]).then(([cashRes, debtRes]) => {
      if (cashRes.data) {
        setWC(cashRes.data);
        setWCDraft({
          dso: cashRes.data.dso != null ? String(cashRes.data.dso) : '',
          dpo: cashRes.data.dpo != null ? String(cashRes.data.dpo) : '',
          dio: cashRes.data.dio != null ? String(cashRes.data.dio) : '',
        });
      }
      if (debtRes.data) setDebtSchedule(debtRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [portco.id]);

  const handleWCDraftChange = (field, value) => {
    setWCDraft(d => ({ ...d, [field]: value }));
    setWCDirty(d => ({ ...d, [field]: true }));
    setWCSaved(s => ({ ...s, [field]: false }));
  };

  const handleWCSave = async (field) => {
    const raw = wcDraft[field].trim();
    const val = raw === '' ? null : parseFloat(raw);
    const now = val != null ? new Date().toISOString() : null;
    const updated = { ...wc, [field]: val, [`${field}_updated_at`]: now };
    setWC(updated);
    setWCDirty(d => ({ ...d, [field]: false }));
    await piApi.saveCashData(portco.id, updated).catch(() => {});
    setWCSaved(s => ({ ...s, [field]: true }));
    setTimeout(() => setWCSaved(s => ({ ...s, [field]: false })), 2000);
  };

  const handleAddDebtRow = async () => {
    const row = {
      ...newDebt,
      beginning_balance: parseFloat(newDebt.beginning_balance) || null,
      scheduled_payment: parseFloat(newDebt.scheduled_payment) || null,
      actual_payment:    parseFloat(newDebt.actual_payment)    || null,
      ending_balance:    parseFloat(newDebt.ending_balance)    || null,
      covenant_headroom: parseFloat(newDebt.covenant_headroom) || null,
    };
    await piApi.addDebtRow(portco.id, row).catch(() => {});
    setDebtSchedule(s => [...s, row]);
    setNewDebt({ period: '', beginning_balance: '', scheduled_payment: '', actual_payment: '', ending_balance: '', covenant_headroom: '' });
    setShowAddDebt(false);
  };

  const handleAnalyzeCash = async () => {
    setAiLoading(true);
    try {
      const res = await piApi.analyzeCashPosition(portco.id);
      setAiAnalysis(res.data.analysis);
    } catch (e) { console.error('[Cash] AI error:', e); }
    finally { setAiLoading(false); }
  };

  const toggleTaxStatus = async (flag) => {
    if (!canEdit) return;
    const next = { monitor: 'action_needed', action_needed: 'resolved', resolved: 'monitor' }[flag.status];
    const updated = taxFlags.map(f => f.id === flag.id ? { ...f, status: next } : f);
    setTaxFlags(updated);
    await piApi.saveTaxFlags(portco.id, updated).catch(() => {});
  };

  if (loading) return <div className="pi-loading" style={{ padding: 40 }}><div className="pi-spinner" /></div>;

  return (
    <div style={{ padding: '24px 28px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>

        {/* Working Capital */}
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Working Capital Metrics</h3>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12, marginTop: 0 }}>
            Data entered manually. Connect an accounting integration when available for automatic updates.{' '}
            <span
              style={{ color: '#4f46e5', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => navigate(`/pi/portco/${portco.id}/integrations`)}
            >
              View integrations
            </span>
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            {[
              { key: 'dso', label: 'DSO — Days Sales Outstanding' },
              { key: 'dpo', label: 'DPO — Days Payable Outstanding' },
              { key: 'dio', label: 'DIO — Days Inventory Outstanding' },
            ].map(({ key, label }) => {
              const fresh = freshnessInfo(wc[`${key}_updated_at`]);
              return (
                <div key={key} style={{ gridColumn: key === 'dio' ? '1 / -1' : undefined }}>
                  <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    {label.split(' — ')[0]}
                    <span style={{ fontWeight: 400, color: '#94a3b8' }}> — {label.split(' — ')[1]}</span>
                  </label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="number" value={wcDraft[key]} disabled={!canEdit}
                      onChange={e => handleWCDraftChange(key, e.target.value)}
                      placeholder={`Benchmark: ${benchmarks[key] ?? 'N/A'} days`}
                      style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', background: canEdit ? 'white' : '#f8fafc' }}
                    />
                    {wcDirty[key] && canEdit && (
                      <button className="pi-btn-ghost" style={{ fontSize: 11, padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 4 }} onClick={() => handleWCSave(key)}>
                        <Save size={12} /> Save
                      </button>
                    )}
                    {wcSaved[key] && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 500 }}>Saved</span>}
                  </div>
                  <div style={{ fontSize: 12, color: fresh.color, marginTop: 4 }}>{fresh.text}</div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <WCCard label="DSO" value={wc.dso} benchmark={benchmarks.dso} />
            <WCCard label="DPO" value={wc.dpo} benchmark={benchmarks.dpo} />
            <div className="pi-stat-card">
              <div className="pi-stat-value" style={{ fontSize: 22 }}>
                {ccc != null ? ccc : (
                  <span title="Enter DSO, DPO, and DIO values to compute the Cash Conversion Cycle." style={{ cursor: 'help' }}>--</span>
                )}
              </div>
              <div className="pi-stat-label">Cash Conversion Cycle</div>
            </div>
          </div>
        </div>

        {/* Debt Schedule */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: 0 }}>Debt Schedule</h3>
            {canEdit && <button className="pi-btn-ghost" style={{ fontSize: 11 }} onClick={() => setShowAddDebt(s => !s)}><Plus size={12} /> Add Period</button>}
          </div>
          <div className="pi-card">
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#fafbff', borderBottom: '1px solid #f1f5f9' }}>
                    {['Period', 'Begin', 'Sched. Pmt', 'Actual Pmt', 'End Bal', 'Covenant %'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', textAlign: 'left', color: '#64748b', fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {debtSchedule.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={{ padding: '8px 10px', color: '#334155', fontWeight: 500 }}>{row.period}</td>
                      <td style={{ padding: '8px 10px', color: '#64748b' }}>${row.beginning_balance || 0}M</td>
                      <td style={{ padding: '8px 10px', color: '#64748b' }}>${row.scheduled_payment || 0}M</td>
                      <td style={{ padding: '8px 10px', color: '#64748b' }}>${row.actual_payment || 0}M</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600, color: '#334155' }}>${row.ending_balance || 0}M</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span className={`pi-badge ${(row.covenant_headroom || 0) >= 20 ? 'pi-badge-green' : (row.covenant_headroom || 0) >= 10 ? 'pi-badge-amber' : 'pi-badge-red'}`}
                          aria-label={`Covenant headroom: ${row.covenant_headroom || 0}%`}>
                          {row.covenant_headroom || 0}%
                        </span>
                      </td>
                    </tr>
                  ))}
                  {showAddDebt && (
                    <tr>
                      {['period','beginning_balance','scheduled_payment','actual_payment','ending_balance','covenant_headroom'].map((f, i) => (
                        <td key={f} style={{ padding: '6px 8px' }}>
                          <input
                            type={i === 0 ? 'text' : 'number'}
                            value={newDebt[f]}
                            onChange={e => setNewDebt(d => ({ ...d, [f]: e.target.value }))}
                            placeholder={i === 0 ? 'Q1 2025' : '0'}
                            style={{ width: '100%', padding: '5px 7px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 12 }}
                          />
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {debtSchedule.length === 0 && !showAddDebt && (
              <div className="pi-empty" style={{ padding: 20 }}>
                <div className="pi-empty-sub">No debt schedule data yet.</div>
                {canEdit && <button className="pi-btn-ghost" onClick={() => setShowAddDebt(true)}><Plus size={13} /> Add first period</button>}
              </div>
            )}
            {showAddDebt && (
              <div style={{ padding: '8px 12px', borderTop: '1px solid #f1f5f9', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <button className="pi-btn-ghost" onClick={() => setShowAddDebt(false)}>Cancel</button>
                <button className="pi-btn-primary" style={{ fontSize: 12 }} onClick={handleAddDebtRow}>Save Row</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tax Flag Register */}
      <div className="pi-card" style={{ marginBottom: 16 }}>
        <div className="pi-card-header">
          <span className="pi-card-title">Tax Flag Register</span>
        </div>
        <div style={{ padding: '10px 16px 0', background: '#fef3c7', borderBottom: '1px solid #fde68a' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingBottom: 10, fontSize: 12, color: '#92400e' }}>
            <AlertTriangle size={13} />
            These flags are prompts to engage your tax advisor, not tax advice.
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <tbody>
            {taxFlags.map(flag => {
              const cfg = TAX_STATUS_CONFIG[flag.status] || TAX_STATUS_CONFIG.monitor;
              return (
                <tr key={flag.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                  <td style={{ padding: '10px 16px', color: '#334155', fontWeight: 500 }}>{flag.label}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', width: 130 }}>
                    <button
                      className={`pi-badge ${cfg.cls}`}
                      style={{ cursor: canEdit ? 'pointer' : 'default', border: 'none', background: 'inherit' }}
                      onClick={() => toggleTaxStatus(flag)}
                      aria-label={`Tax flag status: ${cfg.label}${canEdit ? '. Click to change.' : ''}`}
                    >
                      {cfg.label}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* AI Analysis */}
      <div className="pi-card">
        <div className="pi-card-header">
          <span className="pi-card-title">Cash Position Analysis</span>
        </div>

        {/* Stale data warning banner */}
        {!allNull && anyStale && (
          <div style={{ padding: '10px 16px', background: '#fef3c7', borderBottom: '1px solid #fde68a' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#92400e' }}>
              <AlertTriangle size={13} />
              One or more working capital figures are more than 90 days old. Analysis results may not reflect current performance. Update your figures above before analyzing.
            </div>
          </div>
        )}

        <div className="pi-card-body">
          {allNull ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                className="pi-ai-btn"
                disabled
                style={{ opacity: 0.4, cursor: 'not-allowed' }}
                title="Enter working capital data above before running an analysis."
                aria-label="Analyze cash position — disabled until working capital data is entered"
              >
                <Sparkles size={13} /> Analyze Cash Position
              </button>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>Enter working capital data above to enable analysis.</span>
            </div>
          ) : (
            <>
              <button className="pi-ai-btn" onClick={handleAnalyzeCash} disabled={aiLoading} aria-label="Analyze cash position with AI">
                <Sparkles size={13} />
                {aiLoading ? 'Analyzing cash position...' : 'Analyze Cash Position'}
              </button>
              {aiLoading && <div className="pi-loading" style={{ padding: '14px 0' }}><div className="pi-spinner" /><span>Analyzing cash position...</span></div>}
              {aiAnalysis && (
                <div style={{ marginTop: 12 }}>
                  <p style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.65, margin: 0 }}>{aiAnalysis}</p>
                  <button className="pi-btn-ghost" style={{ marginTop: 10, fontSize: 12 }} onClick={() => { setAiAnalysis(null); handleAnalyzeCash(); }}>Regenerate</button>
                </div>
              )}
              {!aiLoading && !aiAnalysis && (
                <p style={{ color: '#94a3b8', fontSize: 13, marginTop: 8, marginBottom: 0 }}>
                  Get an AI analysis of your working capital efficiency and cash optimization opportunities.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
